import { useEffect, useCallback, useRef } from "react";
import { Backdrop } from "@mui/material";
import { ReactComponent as CloseIcon } from "components/icons/cross-black.svg";
import { BaseButton } from "components/buttons/BaseButton";
import { PlusButton } from "components/buttons";
import { BASE_BUTTON, WITH_CIRCLE_BUTTON } from "./constants";
import "./style/Popup.scss";

interface IBtnProps extends React.ComponentPropsWithRef<"button"> {
  variant?: number;
  formId?: string;
  title?: string;
}

interface IPopupProps {
  onClose?: () => void;
  isOpen: boolean;
  title?: string | React.ReactNode;
  children: React.ReactNode;
  popupClass?: string;
  btnProps?: IBtnProps;
}

export const Popup = (props: IPopupProps) => {
  const {
    onClose,
    isOpen,
    title,
    children,
    popupClass,
    btnProps: {
      variant = undefined,
      title: btnTitle = undefined,
      onClick = undefined,
      disabled = undefined,
      formId = undefined,
      type = undefined
    } = {}
  } = props;
  const popupRef = useRef<HTMLInputElement>(null);

  const keyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && onClose) {
        onClose();
      }
    },
    [onClose, isOpen]
  );

  useEffect(() => {
    document.addEventListener("keydown", keyPress);

    return () => document.removeEventListener("keydown", keyPress);
  }, [keyPress]);

  useEffect(() => {
    if (!document.body) {
      // ignore, happens during page close
    } else if (isOpen) {
      document.body.classList.add("noScroll");
    } else {
      document.body.classList.remove("noScroll");
    }

    return () => document.body?.classList.remove("noScroll");
  }, [isOpen]);

  const closePopup = (e: React.MouseEvent) => {
    if (popupRef.current === e.target && onClose) {
      onClose();
    }
  };

  return (
    <Backdrop
      open={isOpen}
      onClick={closePopup}
      ref={popupRef}
      sx={{
        zIndex: "var(--popupZ)"
      }}
      className={popupClass}
    >
      <div className="Popup">
        {title && (
          <div className="Popup-Header">
            {typeof title === "string" ? (
              <h4>{title}</h4>
            ) : title}
          </div>
        )}
        {onClose && <button className="Popup-Close" onClick={onClose}>
          <CloseIcon />
        </button>}
        <div className="Popup-Content">
          {children}
        </div>
        {variant === BASE_BUTTON && (
          <div className="Popup-BtnBase">
            <BaseButton
              onClick={onClick}
              disabled={disabled}
              form={formId}
              type={type}
            >{btnTitle}</BaseButton>
          </div>
        )}
        {variant === WITH_CIRCLE_BUTTON && (
          <div className="Popup-BtnWithCircle">
            <PlusButton onClick={onClick} hint={btnTitle} />
            <button onClick={onClick}>{btnTitle}</button>
          </div>
        )}
      </div>
    </Backdrop>
  );
};
